import React from 'react';
import notify from 'devextreme/ui/notify';
import './stopwatch.scss';

// Components
import StopwatchSetup from './components/StopwatchSetup';
import StopwatchToolbar from './components/StopwatchToolbar';
import StopwatchGrid from './components/StopwatchGrid';
import StopwatchPopups from './components/StopwatchPopups';

// Hooks
import { useStopwatchState } from './hooks/useStopwatchState';
import { useStopwatchActions } from './hooks/useStopwatchActions';
import { useNavigate } from 'react-router-dom';

// Hauptkomponente
export const Stopwatch = () => {
    const state = useStopwatchState();
    const actions = useStopwatchActions(state);
    const navigate = useNavigate();
    const {
        lanes, rows, grid, isSetupMode, isLoading,
        activeStopwatches, recordedTimes, currentTimes, lapTimes,
        athleteData, swapMode, firstSwapCell,
        sessionName, savedSessions, currentSessionId, autoSaveEnabled,
        showAthleteSelector, showUnknownTimePopup, showExportPopup,
        showSaveSessionDialog, showLoadSessionDialog, unknownTimes,
        setSessionName, setAutoSaveEnabled, setSwapMode, setFirstSwapCell,
        setShowSaveSessionDialog, setShowLoadSessionDialog,
        setShowAthleteSelector, setShowUnknownTimePopup, setShowExportPopup
    } = state;

    const {
        initializeGrid, startRow, stopRow, toggleStopwatch, restartStopwatch,
        recordTime, selectAthlete, handleSwapAthlete, recordUnknownTime,
        assignTimeToAthlete, handleSaveSession, handleLoadSession,
        handleDeleteSession, handleUpdateCurrentSession, handleManualSave,
        handleClearSavedState, handleResetToSetup, handleEditCell,
        handleLoadTemporaryState
    } = actions;

    // Loading Screen
    if (isLoading) {
        return (
            <div className="stopwatch-loading">
                <div className="loading-container">
                    <h2>Stoppuhr-System wird geladen...</h2>
                    <div className="loading-spinner"></div>
                </div>
            </div>
        );
    }

    // Setup Mode
    if (isSetupMode) {
        return (
            <StopwatchSetup
                lanes={lanes}
                setLanes={(value) => state.setLanes(value)}
                rows={rows}
                setRows={(value) => state.setRows(value)}
                sessionName={sessionName}
                setSessionName={setSessionName}
                savedSessions={savedSessions}
                currentSessionId={currentSessionId}
                onInitializeGrid={() => {
                    if (!sessionName.trim()) {
                        notify('Bitte geben Sie einen Namen für die Sitzung ein', 'warning');
                        return;
                    }
                    initializeGrid();
                }}
                onLoadSession={handleLoadSession}
                onDeleteSession={handleDeleteSession}
                onClearSavedState={handleClearSavedState}
                onLoadTemporaryState={handleLoadTemporaryState}
                navigate={navigate}
            />
        );
    }

    // Main Application
    return (
        <div className="stopwatch-container" data-lanes={lanes}>
            <StopwatchToolbar
                rows={rows}
                lanes={lanes}
                currentSessionId={currentSessionId}
                savedSessions={savedSessions}
                autoSaveEnabled={autoSaveEnabled}
                swapMode={swapMode}
                grid={grid}
                onToggleAutoSave={() => setAutoSaveEnabled(!autoSaveEnabled)}
                onManualSave={handleManualSave}
                onShowSaveSessionDialog={() => setShowSaveSessionDialog(true)}
                onShowLoadSessionDialog={() => setShowLoadSessionDialog(true)}
                onCreateNewSession={() => {
                    if (currentSessionId) {
                        handleUpdateCurrentSession();
                    }
                    state.setIsSetupMode(true);
                }}
                onUpdateCurrentSession={handleUpdateCurrentSession}
                onToggleSwapMode={() => {
                    setSwapMode(!swapMode);
                    setFirstSwapCell(null);
                }}
                onRecordUnknownTime={recordUnknownTime}
                onExportResults={() => setShowExportPopup(true)}
               
            />

            <StopwatchGrid
                grid={grid}
                rows={rows}
                lanes={lanes}
                activeStopwatches={activeStopwatches}
                recordedTimes={recordedTimes}
                currentTimes={currentTimes}
                lapTimes={lapTimes}
                onStartRow={startRow}
                onStopRow={stopRow}
                onToggleStopwatch={toggleStopwatch}
                onRestartStopwatch={restartStopwatch}
                onRecordTime={recordTime}
                onEditCell={handleEditCell}
                onSwapAthlete={handleSwapAthlete}
                swapMode={swapMode}
                firstSwapCell={firstSwapCell}
            />

            <StopwatchPopups
                // Athlete Selector
                showAthleteSelector={showAthleteSelector}
                onHideAthleteSelector={() => setShowAthleteSelector(false)}
                athleteData={athleteData}
                onSelectAthlete={selectAthlete}
                
                // Unknown Times
                showUnknownTimePopup={showUnknownTimePopup}
                onHideUnknownTimePopup={() => setShowUnknownTimePopup(false)}
                unknownTimes={unknownTimes}
                grid={grid}
                onAssignTimeToAthlete={assignTimeToAthlete}
                
                // Export
                showExportPopup={showExportPopup}
                onHideExportPopup={() => setShowExportPopup(false)}
                rows={rows}
                lanes={lanes}
                recordedTimes={recordedTimes}
                
                // Save Session
                showSaveSessionDialog={showSaveSessionDialog}
                onHideSaveSessionDialog={() => {
                    setShowSaveSessionDialog(false);
                    setSessionName('');
                }}
                sessionName={sessionName}
                onSessionNameChange={setSessionName}
                onSaveSession={handleSaveSession}
                
                // Load Session
                showLoadSessionDialog={showLoadSessionDialog}
                onHideLoadSessionDialog={() => setShowLoadSessionDialog(false)}
                savedSessions={savedSessions}
                currentSessionId={currentSessionId}
                onLoadSession={handleLoadSession}
                onDeleteSession={handleDeleteSession}
            />
        </div>
    );
};

export default Stopwatch;
